import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { useFormik } from 'formik';
import React, { useState, useEffect, useRef, FC } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import infoSVG from '../../../assets/icons/info.svg';
import ExitSVG from '../../../assets/icons/x-close.svg';
import {
  OptionButton,
  ActionButton,
  InlineButton,
} from 'src/commons/components/Button';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import { CheckBox } from 'src/commons/components/CheckBox';
import { DateDropDown } from 'src/commons/components/DateDropDown';
import FileDownloadLink from 'src/commons/components/FileDownloadLink';
import Heading from 'src/commons/components/Heading';
import SubHeading from 'src/commons/components/SubHeading';
import {
  ApplicationStatus,
  CARD_BREAKPOINT,
  colors,
  LegalDocumentContexts,
  MOBILE_BREAKPOINT,
  MOBILE_WIDTH,
  SMALL_MOBILE_BREAKPOINT,
  SelectedBankAccount,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { GraphQLResponse } from 'src/commons/data/graphql';
import {
  getCreditCardLegalDocuments,
  AutoPayPreferencesPayload,
} from 'src/commons/data/queries';
import { logError } from 'src/commons/utilities/logging';

import { AutoPayIcon } from './AutoPayIcon';

// Interface --------------------------------------------------------------------------------------
export interface AutoPayPreferencesProps {
  showApplicationFetchError: boolean;
  setShowErrorModal: Function;
  applicationStatus: ApplicationStatus;
  selectedBankAccount: SelectedBankAccount;
  showSetupPage: Function;
  saveAutoPayPreferencesEnrolled: (
    payload: AutoPayPreferencesPayload
  ) => Promise<GraphQLResponse>;
  saveAutoPayPreferencesDeclined: Function;
  submitAutoPayPage: Function;
  submitting: boolean;
}

interface FormValues {
  paymentAmountType: string;
  monthlyPaymentDay: string;
  autoPayAgreement: boolean;
}

interface StyledProps {
  selected?: boolean;
  showToolTip?: boolean;
  showModal?: boolean;
}

// #region -- Styles ------------------------------------------------------------------------------
const AutoPaySubHeading = styled(SubHeading)`
  min-height: 3.25rem;
  width: 100%;
  max-width: 25rem;
  margin: 0.5625rem 0 0;
  padding: 0 0.5rem;
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 21.4375rem;
    padding: 0 0.25rem;
  }
`;
AutoPaySubHeading.displayName = 'AutoPaySubHeading';

const PreferencesContainer = styled.div`
  width: 100%;
  max-width: 25rem;
  margin: 1.625rem;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 21.4375rem;
  }
`;
PreferencesContainer.displayName = 'PreferencesContainer';

const AmountOptionButtonContainer = styled.div`
  width: 100%;
  margin: 0 0 0.875rem;
  display: flex;
  justify-content: space-between;
  button:first-child {
    margin: 0 0.1875rem 0 0;
  }
  button:last-child {
    margin: 0 0 0 0.1875rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 1.3125rem;
  }
`;
AmountOptionButtonContainer.displayName = 'AmountOptionButtonContainer';

const AmountOptionButton = styled(OptionButton)`
  height: unset;
  min-height: 6.1875rem;
  border-radius: 0.625rem;
  padding: 1.1875rem 0.375rem;
  display: flex;
  flex: 1;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 12.5rem;
    padding: 0.5rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.5rem 0.5rem 0.625rem;
  }
`;
AmountOptionButton.displayName = 'AmountOptionButton';

const AmountOptionText = styled.p`
  width: 100%;
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.375rem;
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0.3125rem;
    font-size: 0.8125rem;
  }
`;
AmountOptionText.displayName = 'AmountOptionText';

const AmountOptionTextBold = styled(AmountOptionText)`
  padding: 0 0 0.25rem;
  display: inline-block;
  font-size: 1rem;
  font-weight: 700;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 0 0.25rem;
    font-size: 0.9375rem;
  }
  @media (max-width: ${SMALL_MOBILE_BREAKPOINT}) {
    padding: 0.25rem;
  }
`;
AmountOptionTextBold.displayName = 'AmountOptionTextBold';

const AgreementsContainer = styled.div`
  width: 100%;
  min-height: 2.75rem;
  margin: 0 auto 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 4.125rem;
  }
`;
AgreementsContainer.displayName = 'AgreementsContainer';

const AutoPayAgreementCheckBox = styled(CheckBox)`
  border: 0.09375rem solid ${colors.UI_PRIMARY_ACCENT};
`;
AutoPayAgreementCheckBox.displayName = 'AutoPayAgreementCheckBox';

const AutoPayAgreementDocumentLink = styled(FileDownloadLink)`
  text-decoration: underline;
  color: ${colors.TEXT_PRIMARY};
`;
AutoPayAgreementDocumentLink.displayName = 'AutoPayAgreementDocumentLink';

const AutoPayAgreementParagraph = styled.p`
  line-height: 1.375rem;
  font-size: 1rem;
  text-align: left;
  color: ${colors.TEXT_PRIMARY};
`;
AutoPayAgreementParagraph.displayName = 'AutoPayAgreementParagraph';

const PreferenceSaveButton = styled(ActionButton)`
  padding: 0;
  width: 100%;
`;
PreferenceSaveButton.displayName = 'PreferenceSaveButton';

const DeclineButton = styled(InlineButton)`
  margin: 1.5rem 1rem;
  color: ${colors.TEXT_ANCHOR};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus:not(:hover, :focus-visible) {
    text-decoration: none;
    outline: none;
    margin: 1.5rem 1rem;
    padding: auto;
  }
  &:focus-visible {
    text-decoration: none;
    outline: 0.125rem solid ${colors.UI_SECONDARY_ACCENT};
    border-radius: 0.125rem;
    padding: 0.0625rem;
    margin: 1.4375rem 1rem;
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 1rem 1rem 1.1875rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1rem 1rem 0;
  }
`;
DeclineButton.displayName = 'DeclineButton';

// ToolTip styles
// Contains ToolTip - click will display ToolTip
const ToolTipButton = styled.button`
  // z-index set to 3 to have mobile infotip modal be over the modal mask
  z-index: 3;
  position: relative;
  height: 1.5rem;
  width: 1.5rem;
  margin: 0 0 0 0.0625rem;
  cursor: pointer;
  &:focus-visible {
    border-radius: 0.25rem;
    outline: 0.125rem solid ${colors.UI_PRIMARY};
    outline-offset: -0.0625rem;
  }
`;
ToolTipButton.displayName = 'ToolTipButton';

const ToolTipContainer = styled.div<StyledProps>`
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s ease-in-out;
  ${(p: StyledProps) =>
    p.showToolTip &&
    `
    cursor: default;
    pointer-events: auto;
    opacity: 1;
  `};
`;
ToolTipContainer.displayName = 'ToolTipContainer';

const InfoIcon = styled(infoSVG)`
  margin: 0 0 -0.1875rem;
`;
InfoIcon.displayName = 'InfoIcon';

// info section that pops up when hovering over
const ToolTip = styled.div`
  position: absolute;
  min-height: 11.6875rem;
  width: 18.25rem;
  margin: 0.625rem 0 0 -8.75rem;
  border-radius: 0.25rem;
  padding: 1rem 1.25rem;
  box-shadow: 0 0.01625rem 0.25rem 0 #dadada;
  text-align: left;
  background-color: ${colors.DROPDOWN_BACKGROUND};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    z-index: 3;
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    overflow: scroll;
    max-height: 23.25rem; // allows overflow scrolling when necessary
    width: 21.4375rem;
    margin: auto;
    padding: 3rem 3rem 3rem 1.5rem;
  }
  &:focus-visible {
    outline: none;
  }
`;
ToolTip.displayName = 'ToolTip';

// caret that extends towards the info icon from container
const ToolTipCaret = styled.div`
  position: absolute;
  height: 2rem;
  width: 2rem;
  margin: 0 0 0 -0.25rem;
  box-shadow: 0 0.01625rem 0.25rem 0 #dadada;
  transform: rotate(45deg);
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
ToolTipCaret.displayName = 'ToolTipCaret';

// hides box shadow from main ToolTip
const CaretMask = styled.div`
  z-index: 2;
  position: absolute;
  height: 1.5rem;
  width: 1.5rem;
  margin: -0.125rem 0 0 0;
  transform: rotate(45deg);
  background: ${colors.DROPDOWN_BACKGROUND};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
CaretMask.displayName = 'CaretMask';

const ToolTipText = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.53125rem;
  color: ${colors.TEXT_PRIMARY};
  &:first-of-type {
    margin: 0 0 0.625rem;
    font-weight: 700;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;
ToolTipText.displayName = 'ToolTipText';

// X button closes ToolTip on mobile
const ExitButton = styled(ExitSVG)`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: absolute;
    top: 0.9375rem;
    right: 0.9375rem;
    display: inline;
  }
`;
ExitButton.displayName = 'ExitButton';

const ModalMask = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    z-index: 2;
    position: fixed;
    top: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    background: #333333;
    opacity: 0.25;
  }
`;
ModalMask.displayName = 'ModalMask';
// #endregion

const AutoPayToolTip = (showToolTip: boolean, closeToolTip: { (): void }) => (
  <ToolTipContainer id="autoPayToolTip" showToolTip={showToolTip}>
    <ToolTipCaret />
    <CaretMask />
    <ToolTip id="autopay-tooltip-content">
      <ExitButton id="exit-button" onClick={closeToolTip} />
      {showToolTip && (
        <>
          <ToolTipText>You’re in control of your payments</ToolTipText>
          <ToolTipText>
            Don’t worry, you can always turn off AutoPay or change your payment
            option later. You can also make extra payments whenever you like.
            With AutoPay, you’re in control.
          </ToolTipText>
        </>
      )}
    </ToolTip>
  </ToolTipContainer>
);

enum PaymentAmountType {
  MINIMUM_PAYMENT = 'MINIMUM_PAYMENT',
  STATEMENT_BALANCE = 'STATEMENT_BALANCE',
}

const DROPDOWN_PLACEHOLDER = 'Select your monthly payment date';

// Component --------------------------------------------------------------------------------------
export const AutoPayPreferences: FC<AutoPayPreferencesProps> = ({
  showApplicationFetchError,
  setShowErrorModal,
  applicationStatus,
  selectedBankAccount,
  showSetupPage,
  saveAutoPayPreferencesEnrolled,
  saveAutoPayPreferencesDeclined,
  submitAutoPayPage,
  submitting,
}) => {
  // State ----------------------------------------------------------------------------------------
  const [showToolTip, setShowToolTip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [checkingBankConnected, setCheckingBankConnected] = useState(true);
  const [fetchingAutoPayAgreement, setFetchingAutoPayAgreement] =
    useState(false);
  const [showAgreementFetchFailure, setShowAgreementFetchFailure] =
    useState(false);
  const [autoPayAgreement, setAutoPayAgreement] = useState({
    downloadUrl: '',
    name: '',
    title: '',
    version: undefined,
    viewUrl: '',
  });

  // Hooks ----------------------------------------------------------------------------------------
  const toolTipRef = useRef(null);

  useEffect(() => {
    if (
      applicationStatus ===
        ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION &&
      !selectedBankAccount
    ) {
      showSetupPage();
    } else {
      setCheckingBankConnected(false);
      fetchAutoPayAgreement();
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (toolTipRef && toolTipRef.current && toolTipRef.current !== e.target) {
        setShowToolTip(false);
        setShowModal(false);
      }
    };
    if (showToolTip) {
      window.addEventListener('click', handleOutsideClick);
    }
    return () => {
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [showToolTip]);

  // scroll lock for mobile modal
  useEffect(() => {
    if (showModal && width < MOBILE_WIDTH) {
      disableBodyScroll(document.body);
    } else {
      enableBodyScroll(document.body);
    }
    return () => {
      // clean up any locks that may have slipped through
      clearAllBodyScrollLocks();
    };
  }, [showModal]);

  // Formik ---------------------------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      paymentAmountType: PaymentAmountType.MINIMUM_PAYMENT,
      monthlyPaymentDay: '',
      autoPayAgreement: false,
    },
    validationSchema: Yup.object().shape({
      paymentAmountType: Yup.string()
        .min(1, 'Select your payment option')
        .required('Select your payment option'),
      monthlyPaymentDay: Yup.string().required('Select a monthly payment date'),
      autoPayAgreement: Yup.boolean().oneOf(
        [true],
        'Please review and agree to the document above'
      ),
    }),
    onSubmit: async (values: FormValues, { setSubmitting }) => {
      const { paymentAmountType, monthlyPaymentDay } = values;
      const { name, version } = autoPayAgreement;
      const createAutoPayPreferencesPayload = {
        legalDocumentsToSave: [
          {
            name,
            version,
          },
        ],
        monthlyPaymentDay: parseInt(monthlyPaymentDay, 10),
        paymentAmountType,
      };

      try {
        setSubmitting(true);
        await saveAutoPayPreferencesEnrolled(createAutoPayPreferencesPayload);
        submitAutoPayPage();
      } catch (err) {
        setShowErrorModal(true);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Functions -------------------------------------------------------------------------------------

  const handleClick = () => {
    setShowToolTip(true);
    if (width < MOBILE_WIDTH) {
      setShowModal(true);
    }
  };

  const closeToolTip = () => {
    setShowToolTip(false);
    setShowModal(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showToolTip) {
      // Close expanded tooltip on ESC key
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeToolTip();
      }
      // Persist expanded tooltip on Space/Enter
      if (e.key === 'Enter' || e.key === 'Space' || e.code === 'Space') {
        e.preventDefault();
        setShowToolTip(true);
      }
    }
  };

  const handleSelectedPaymentDate = () => {
    const value = formik.values.monthlyPaymentDay;
    if (value !== '') {
      return `Payment date - ${value} of each month`;
    }
    return DROPDOWN_PLACEHOLDER;
  };

  // Get legal document for AutoPay Preferences
  async function fetchAutoPayAgreement() {
    // Show spinner
    setFetchingAutoPayAgreement(true);
    try {
      // Fetch legal docs
      const response: GraphQLResponse = await getCreditCardLegalDocuments(
        LegalDocumentContexts.AUTOPAY_PREFERENCE
      );
      const { data, errors } = response;
      if (data && data.getCreditCardLegalDocuments) {
        setAutoPayAgreement(data.getCreditCardLegalDocuments[0]);
      }
      if (errors) {
        setShowAgreementFetchFailure(true);
      }
    } catch (err: any) {
      setShowAgreementFetchFailure(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'ComponentError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'AutoPayPreferences',
          function: 'fetchAutoPayAgreement',
        });
      }
    } finally {
      // Always turn fetchingAutoPayAgreement off
      setFetchingAutoPayAgreement(false);
    }
  }

  // Render ----------------------------------------------------------------------------------------
  if (checkingBankConnected || fetchingAutoPayAgreement) {
    return <LoadingCard />;
  }

  if (showAgreementFetchFailure || showApplicationFetchError) {
    return <ErrorCard />;
  }

  return (
    <Card id="autopay-preference-card">
      <AutoPayIcon />
      <Heading>AutoPay preferences</Heading>
      <AutoPaySubHeading>
        Avoid late fees by choosing a payment option and date that works for you
        <ToolTipButton
          id="info-toggle-area"
          aria-hidden={!showToolTip}
          aria-describedby="autopay-tooltip-content"
          type="button"
          onClick={handleClick}
          onBlur={closeToolTip}
          onKeyDown={(e) => handleKeyDown(e)}
        >
          <InfoIcon id="info-icon" ref={toolTipRef} />
          {AutoPayToolTip(showToolTip, closeToolTip)}
        </ToolTipButton>
      </AutoPaySubHeading>
      <PreferencesContainer>
        <AmountOptionButtonContainer>
          <AmountOptionButton
            id="minimum-payment"
            aria-label="Set the minimum payment as your preference for the AutoPay amount"
            type="button"
            selected={
              formik.values.paymentAmountType ===
              PaymentAmountType.MINIMUM_PAYMENT
            }
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
            onClick={() =>
              formik.setFieldValue(
                'paymentAmountType',
                PaymentAmountType.MINIMUM_PAYMENT
              )
            }
          >
            <AmountOptionTextBold>Minimum payment</AmountOptionTextBold>
            <AmountOptionText>
              The least you can pay to avoid a late fee
            </AmountOptionText>
          </AmountOptionButton>
          <AmountOptionButton
            id="statement-balance"
            aria-label="Set the statement balance as your preference for the AutoPay amount"
            type="button"
            selected={
              formik.values.paymentAmountType ===
              PaymentAmountType.STATEMENT_BALANCE
            }
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
            onClick={() =>
              formik.setFieldValue(
                'paymentAmountType',
                PaymentAmountType.STATEMENT_BALANCE
              )
            }
          >
            <AmountOptionTextBold>Statement balance</AmountOptionTextBold>
            <AmountOptionText>
              Everything you owe as of your last statement
            </AmountOptionText>
          </AmountOptionButton>
        </AmountOptionButtonContainer>
        <DateDropDown
          name="monthlyPaymentDay"
          value={formik.values.monthlyPaymentDay}
          label={DROPDOWN_PLACEHOLDER}
          onChange={formik.handleChange}
          formatSelection={handleSelectedPaymentDate}
          error={formik.errors.monthlyPaymentDay}
          touched={formik.touched.monthlyPaymentDay}
          disabled={formik.isSubmitting}
          onBlur={formik.handleBlur}
        />
        <AgreementsContainer>
          <AutoPayAgreementCheckBox
            name="autoPayAgreement"
            value={formik.values.autoPayAgreement}
            setFieldValue={formik.setFieldValue}
            setFieldTouched={() => formik.setFieldTouched('autoPayAgreement')}
            disabled={formik.isSubmitting}
            error={formik.errors.autoPayAgreement}
            touched={formik.touched.autoPayAgreement}
            onChange={formik.handleChange}
            ariaLabelledBy="autopay-checkbox-text"
          >
            <AutoPayAgreementParagraph id="autopay-checkbox-text">
              By clicking the box I confirm that I have read and agree by
              electronic signature to the{' '}
              <AutoPayAgreementDocumentLink
                id="autopay-agreement-document"
                href={autoPayAgreement.viewUrl}
                target="_blank"
              >
                AutoPay Agreement
              </AutoPayAgreementDocumentLink>
              .
            </AutoPayAgreementParagraph>
          </AutoPayAgreementCheckBox>
        </AgreementsContainer>
        <PreferenceSaveButton
          id="preference-save-button"
          type="submit"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.values.autoPayAgreement || formik.isSubmitting}
        >
          Save AutoPay preferences
        </PreferenceSaveButton>
        <DeclineButton
          id="autopay-preferences-decline-button"
          aria-label="Skip AutoPay for now"
          onClick={saveAutoPayPreferencesDeclined}
          disabled={submitting}
        >
          Skip AutoPay for now
        </DeclineButton>
      </PreferencesContainer>
      {showModal && <ModalMask id="modal-mask" onClick={closeToolTip} />}
    </Card>
  );
};
